import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { addDays } from "date-fns";
import { api, errMsg } from "@/lib/api";
import { ABSENCE_TYPES, SHIFT_KINDS, fmtDate, isoDate } from "@/lib/format";
import { AbsenceForm, AbsenceStatus } from "@/components/AbsenceForm";
import { Button } from "@/components/ui/button";

const MyAbsences = ({ list, onChange }) => {
  const cancel = async (a) => {
    try {
      await api.delete(`/hr/absences/${a.id}`);
      toast.success("Richiesta annullata");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!list.length) return <div className="border bg-white p-4 text-sm text-muted-foreground" data-testid="tech-absences-empty">Nessuna assenza registrata.</div>;
  return (
    <div className="space-y-2" data-testid="tech-absences-list">
      {list.map((a) => (
        <div key={a.id} className="flex items-center gap-3 border bg-white p-3" data-testid={`tech-absence-${a.id}`}>
          <div className="flex-1">
            <div className="text-sm font-semibold">{ABSENCE_TYPES[a.type]}</div>
            <div className="font-mono text-xs text-muted-foreground">{fmtDate(a.date_from)}{a.date_to !== a.date_from && ` → ${fmtDate(a.date_to)}`}</div>
            {a.decision?.note && a.status === "REJECTED" && <div className="mt-1 text-xs text-red-700">{a.decision.note}</div>}
          </div>
          <AbsenceStatus status={a.status} />
          {a.status === "REQUESTED" && <Button size="sm" variant="ghost" onClick={() => cancel(a)} data-testid={`tech-absence-cancel-${a.id}`}>Annulla</Button>}
        </div>
      ))}
    </div>
  );
};

export default function TechAbsences() {
  const [list, setList] = useState([]);
  const [shifts, setShifts] = useState([]);
  const today = isoDate(new Date());
  const until = isoDate(addDays(new Date(), 13));
  const load = useCallback(() => {
    api.get("/hr/absences").then((r) => setList(r.data)).catch(() => {});
    api.get("/hr/shifts", { params: { date_from: today, date_to: until } }).then((r) => setShifts(r.data)).catch(() => {});
  }, [today, until]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="tech-absences-page">
      <h1 className="font-display text-3xl font-extrabold">Assenze e turni</h1>
      <p className="mt-1 text-sm text-muted-foreground">Richiedi ferie o permessi: il responsabile riceverà la richiesta da approvare.</p>
      <section className="mt-6 border bg-white p-4">
        <div className="micro-label mb-3">Nuova richiesta</div>
        <AbsenceForm onSaved={load} testPrefix="tech-absence" submitLabel="Invia richiesta" />
      </section>
      <section className="mt-6">
        <div className="micro-label mb-3">Le mie assenze</div>
        <MyAbsences list={list} onChange={load} />
      </section>
      <section className="mt-6" data-testid="tech-shifts">
        <div className="micro-label mb-3">Turni prossimi 14 giorni</div>
        {shifts.length === 0 ? <div className="border bg-white p-4 text-sm text-muted-foreground">Nessun turno specifico: orario standard.</div> : (
          <div className="grid grid-cols-2 gap-2">
            {shifts.map((s) => (
              <div key={s.id} className="border bg-white p-3"><div className="font-mono text-xs text-muted-foreground">{fmtDate(s.date)}</div><div className="text-sm font-semibold">{SHIFT_KINDS[s.kind]}</div></div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
